import { GetStaticPaths, GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'

import ChannelGuard from '@/guards/ChannelGuard'
import AssembleLayout from '@/layouts/AssembleLayout'
import MainLayout from '@/layouts/MainLayout'
import { channelMiddleware, siteMiddleware, storeMiddleware } from '@/lib/middlewares'
import { urlHostResolver } from '@/lib/utils'
import { noIndex } from '@/lib/utils/next-seo.config'
import { Channel as ChannelType, Site as SiteType, Store as StoreType } from '@/types'
import StoreView from '@/views/sites/StoreView'

interface Data {
  site?: SiteType
  channel?: ChannelType
  store?: StoreType
}

interface Props {
  initialData?: Data
}

export default function StorePage({ initialData }: Props) {
  const { site: siteData, channel: channelData, store: storeData } = initialData || {}

  // SEO
  const path = `/${channelData?.slug}/${storeData?.slug}`
  const urlSEO = siteData?.domain
    ? `${siteData.domain}${path}`
    : urlHostResolver(siteData?.subdomain, path)

  const commonSEOProps = {
    title: `${storeData?.displayName || 'Loja'}${channelData ? ` | ${channelData?.title}` : ''}`,
    description: `Acesse as vitrines da loja ${storeData?.displayName}`
  }

  return (
    <AssembleLayout
      Layout={MainLayout}
      View={StoreView}
      Guard={ChannelGuard}
      initialData={{ initialData }}>
      <NextSeo
        canonical={urlSEO}
        {...commonSEOProps}
        openGraph={{
          ...commonSEOProps,
          url: urlSEO,
          images: [
            { url: (storeData?.image || channelData?.cover) as string, alt: commonSEOProps.title }
          ]
        }}
        {...(channelData?.privacy !== 'public' ? noIndex : {})}
      />
    </AssembleLayout>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async context => {
  const { data: siteData } = await siteMiddleware(context)
  const { data: channelData } = await channelMiddleware(context)
  const { data: storeData, error: storeDataError } = await storeMiddleware(context)

  return storeDataError?.response?.status === 404
    ? { notFound: true, revalidate: 60 }
    : {
        props: { initialData: { site: siteData, channel: channelData, store: storeData } },
        revalidate: 60
      }
} // revalidate time in seconds
